import { Loader2, X, FileSearch } from "lucide-react";
import FilePreview from "@/modern/components/file-preview";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";
import { getFileExtension } from "@/modern/utils";

export const ImageViewer = () => {
  const {
    file,
    imageLoading,
    showSpinner,
    displayedImageUrl,
    currentImageUrl,
    handleLoadComplete,
    pdfSearchText,
    clearPdfSearch,
  } = useFileDetailModal();

  if (!file) return null;

  const isPDF = getFileExtension(file.name) === "pdf";
  const isImage = isImageFileTyped(file) && !isPDF;

  // For first load when displayedImageUrl is undefined, use currentImageUrl
  // After first load, always use displayedImageUrl to show previous image while new one loads
  const srcToDisplay = displayedImageUrl || (isImage ? currentImageUrl : undefined);

  // Only preload if we have a different URL and displayedImageUrl is set (not first load)
  const shouldPreload = isImage && currentImageUrl && displayedImageUrl && currentImageUrl !== displayedImageUrl;

  return (
    <div className="group relative flex h-full w-full items-center justify-center overflow-hidden">
      {/* Loading spinner overlay - shows after 300ms - only for images */}
      {isImage && showSpinner && imageLoading && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-in fade-in duration-200">
          <Loader2 className="h-12 w-12 animate-spin text-foreground" />
        </div>
      )}

      {/* Active PDF search indicator */}
      {isPDF && pdfSearchText && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-background/95 backdrop-blur-sm border border-border shadow-md max-w-[280px]">
          <FileSearch className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="text-xs text-foreground truncate">
            {pdfSearchText.length > 50 ? pdfSearchText.slice(0, 50) + "..." : pdfSearchText}
          </span>
          <button
            type="button"
            onClick={clearPdfSearch}
            className="flex items-center justify-center w-5 h-5 rounded hover:bg-muted shrink-0 transition-colors"
            aria-label="Clear search"
          >
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        </div>
      )}

      {/* Display file preview - shows the displayedImageUrl (previous image while loading new one) */}
      <FilePreview
        file={file}
        src={srcToDisplay}
        className="relative z-10 h-full w-full max-h-full object-contain rounded-md"
        fileIconClassName="size-24"
        showName={true}
        disableLoadingState={true}
        enableVideoAudioRendering={true}
        isUseFileQueryParam={true}
        searchText={pdfSearchText}
      />

      {/* Preload new image invisibly - only when navigating (not first load) */}
      {shouldPreload && (
        <img
          src={currentImageUrl + "?v=" + Date.now()}
          alt=""
          className="hidden"
          onLoad={handleLoadComplete}
          onError={handleLoadComplete}
        />
      )}
    </div>
  );
};
